/**
 * App Domain Types
 *
 * TypeScript types for the App domain state.
 */

export const CHAT_CLIENT_CONNECTION_STATUSES = {
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
  SILENTLY_RECONNECTING: 'SILENTLY_RECONNECTING',
  SILENTLY_DISCONNECTED: 'SILENTLY_DISCONNECTED',
} as const;

export type ChatClientConnectionStatus =
  (typeof CHAT_CLIENT_CONNECTION_STATUSES)[keyof typeof CHAT_CLIENT_CONNECTION_STATUSES];

export const SESSION_ENDED_REASONS = {
  AGENT_ENDED: 'AGENT_ENDED',
  CONSUMER_NO_REPLY: 'CONSUMER_NO_REPLY',
  TIME_EXPIRED: 'TIME_EXPIRED',
  NEW_SESSION_STARTED: 'NEW_SESSION_STARTED',
  CUSTOMER_ABANDONED: 'CUSTOMER_ABANDONED',
} as const;

export type SessionEndedReason = (typeof SESSION_ENDED_REASONS)[keyof typeof SESSION_ENDED_REASONS];

export interface AgentActivity {
  text: string;
  token: number;
  /** The Beacon tool call the status belongs to. */
  callId: string;
  /** True until that call settles. */
  toolRunning: boolean;
  /** `Date.now()` when shown. */
  shownAt: number;
}

export interface AppState {
  _authError: string | null;
  _provider: string | null;
  _serverError: string | null;
  /** Beacon tool status beside the typing dots; `token` distinguishes publications with the same text. */
  agentActivity: AgentActivity | null;
  appId: string | null;
  appStarted: boolean;
  authenticationPending: boolean;
  cdn: string | null;
  chatClientConnectionStatus: ChatClientConnectionStatus | null;
  clientId: string | null;
  clientReady: boolean;
  customerUrl: string | null;
  estimatedWait: number;
  initialized: boolean;
  isAgentTyping: boolean;
  isNavigating: boolean;
  isSessionEnded: boolean;
  loginDomain: string | null;
  observers: Record<string, string[]>;
  orgDomain: string | null;
  orgId: string | null;
  pageLoadedAt: number;
  provider: string | null;
  ready: boolean;
  sessionEndedReason: SessionEndedReason | null;
  sessionStartedAt: number | null;
  vaultId: string | null;
  vaultType: string | null;
}

/**
 * Initial app state
 */
export function getInitialAppState(): AppState {
  return {
    _authError: null,
    _provider: null,
    _serverError: null,
    agentActivity: null,
    appId: null,
    appStarted: false,
    authenticationPending: false,
    cdn: null,
    chatClientConnectionStatus: null,
    clientId: null,
    clientReady: false,
    customerUrl: null,
    estimatedWait: 0,
    initialized: false,
    isAgentTyping: false,
    isNavigating: false,
    isSessionEnded: false,
    loginDomain: null,
    observers: {},
    orgDomain: null,
    orgId: null,
    pageLoadedAt: Date.now(),
    provider: null,
    ready: false,
    sessionEndedReason: null,
    sessionStartedAt: null,
    vaultId: null,
    vaultType: null,
  };
}
